/*
 * tiered-pricing.js
 * Displays predicted per-item / line-total pricing for native Shopify automatic
 * quantity discounts, based on how many eligible items are (or would be) in the cart.
 *
 * Config comes from window.TIERED_PRICING (rendered in snippets/product-buttons.liquid).
 * This file only affects DISPLAY — Shopify's automatic discount still does the real charging.
 */
(function () {
  if (window.__tieredPricingInit) return;
  window.__tieredPricingInit = true;

  var CFG = window.TIERED_PRICING || {};
  var TIERS = (CFG.tiers || []).slice().sort(function (a, b) {
    return a.minQty - b.minQty;
  });
  var MONEY_FORMAT = CFG.useCurrencyCode
    ? CFG.moneyWithCurrencyFormat || CFG.moneyFormat
    : CFG.moneyFormat;
  var SHOW_LINE_TOTAL = CFG.showLineTotal !== false;
  var ITEM_NOUN = CFG.itemNoun || 'item';
  var ELIGIBLE_PROP = '_td_eligible';

  var eligibleQtyInCart = 0;

  /* ---- Standard Shopify money formatter (matches your theme's money format) ---- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';

    function fmt(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        '$1' + thousands,
      );
      var rest = parts[1] ? decimal + parts[1] : '';
      return dollars + rest;
    }

    var match = formatString.match(placeholderRegex);
    switch (match ? match[1] : 'amount') {
      case 'amount':
        value = fmt(cents, 2);
        break;
      case 'amount_no_decimals':
        value = fmt(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = fmt(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = fmt(cents, 0, '.', '');
        break;
      case 'amount_with_space_separator':
        value = fmt(cents, 2, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = fmt(cents, 2, "'", '.');
        break;
      default:
        value = fmt(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  /* ---- Tier selection + discount math (all in cents) ---- */
  function pickTier(qty) {
    var chosen = null;
    for (var i = 0; i < TIERS.length; i++) {
      if (qty >= TIERS[i].minQty) chosen = TIERS[i]; // highest qualifying tier wins
    }
    return chosen;
  }

  function applyTier(baseCents, tier) {
    if (!tier) return baseCents;
    if (tier.type === 'amount') {
      return Math.max(0, baseCents - Math.round(tier.value * 100)); // value = money off per item
    }
    return Math.ceil(baseCents * (1 - tier.value / 100)); // percentage off
  }

  /* ---- Read current cart, count eligible items via the hidden line-item property ---- */
  function refreshCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var q = 0;
        (cart.items || []).forEach(function (it) {
          var p = it.properties || {};
          if (p[ELIGIBLE_PROP]) q += it.quantity;
        });
        eligibleQtyInCart = q;
        return cart;
      })
      .catch(function () {
        return null;
      });
  }

  /* ---- Per-button helpers ---- */
  function getBaseUnitCents(el) {
    var form = el.closest('form');
    var pf = el.closest('product-form');
    var jsonEl = pf && pf.querySelector('[data-product-json]');
    var idInput = form && form.querySelector('[name="id"]');
    if (jsonEl && idInput) {
      try {
        var product = JSON.parse(jsonEl.textContent);
        var vid = parseInt(idInput.value, 10);
        for (var i = 0; i < (product.variants || []).length; i++) {
          if (product.variants[i].id === vid) return product.variants[i].price; // cents
        }
      } catch (e) {
        /* fall through */
      }
    }
    var wpd = el.getAttribute('data-wpd-variant-price');
    return wpd ? parseInt(wpd, 10) : null;
  }

  // The wpd app owns [data-button-price] and rewrites its innerHTML, so our discounted
  // price lives in a sibling element (inserted right before it) that wpd never touches.
  function ensurePriceDisplay(el) {
    var d = el.previousElementSibling;
    if (d && d.hasAttribute && d.hasAttribute('data-tiered-display')) return d;
    d = document.createElement('span');
    d.setAttribute('data-tiered-display', '');
    d.hidden = true;
    d.innerHTML =
      '<span data-tiered-current></span><s class="td-was" data-tiered-was></s>';
    el.parentNode.insertBefore(d, el);
    return d;
  }

  function getAddQty(el) {
    var form = el.closest('form');
    var qtyInput =
      (form && form.querySelector('[name="quantity"]')) ||
      document.querySelector('[name="quantity"]');
    var q = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    return isNaN(q) || q < 1 ? 1 : q;
  }

  /* ---- Repaint every price button + its banner ---- */
  function updateAll() {
    document.querySelectorAll('[data-button-price]').forEach(function (el) {
      var eligible = el.getAttribute('data-tiered-eligible') === 'true';
      var baseUnit = getBaseUnitCents(el);
      if (baseUnit == null) return;

      var addQty = getAddQty(el);

      // What tier would the cart land in if we added this product now?
      var projectedQty = eligibleQtyInCart + (eligible ? addQty : 0);
      var tier = eligible ? pickTier(projectedQty) : null;
      var unit = applyTier(baseUnit, tier);
      var discounted = tier && unit < baseUnit;

      var shownNow = SHOW_LINE_TOTAL ? unit * addQty : unit;
      var shownBase = SHOW_LINE_TOTAL ? baseUnit * addQty : baseUnit;

      // Show our discounted total in the sibling and hide the wpd span while a tier applies;
      // otherwise hide ours and let wpd render the normal price. wpd rewrites innerHTML (not
      // style), so hiding via style.display survives its re-renders.
      var display = ensurePriceDisplay(el);
      if (discounted) {
        display.querySelector('[data-tiered-current]').innerHTML = formatMoney(
          shownNow,
          MONEY_FORMAT,
        );
        display.querySelector('[data-tiered-was]').innerHTML = formatMoney(
          shownBase,
          MONEY_FORMAT,
        );
        display.hidden = false;
        el.style.display = 'none';
        el.setAttribute('data-tiered-active', 'true');
      } else {
        display.hidden = true;
        el.style.display = '';
        el.removeAttribute('data-tiered-active');
      }

      // "Add N more to unlock the next tier" nudge. It gets relocated above the quantity
      // field (see placeNudge), so fall back to a document-wide lookup when it's no longer
      // inside this product-form.
      var scope = el.closest('product-form') || document;
      var nudge =
        scope.querySelector('[data-td-nudge]') ||
        document.querySelector('[data-td-nudge]');
      if (nudge) {
        var next = null;
        for (var t = 0; t < TIERS.length; t++) {
          if (TIERS[t].minQty > projectedQty) {
            next = TIERS[t];
            break;
          } // TIERS is ascending
        }
        // Price portion of the nudge. Once a discount is actually active (2nd tier onward),
        // show the original price struck through next to the new per-item price; before that
        // (1st tier, no discount yet) just show the base price. `unit`/`discounted` from above.
        var pricePart;
        if (discounted) {
          pricePart =
            '<s style="opacity:.6">' +
            formatMoney(baseUnit, MONEY_FORMAT) +
            '</s> <strong>' +
            formatMoney(unit, MONEY_FORMAT) +
            '</strong> per ' +
            ITEM_NOUN +
            '.';
        } else {
          pricePart =
            '<strong>' +
            formatMoney(baseUnit, MONEY_FORMAT) +
            '</strong> per ' +
            ITEM_NOUN +
            '.';
        }
        if (eligible && next) {
          var remaining = next.minQty - projectedQty;
          // % off at the next tier: use the tier value directly for percentage tiers,
          // otherwise derive it from the amount discount vs. the base price.
          var pctOff =
            next.type === 'percentage'
              ? next.value
              : Math.round(
                  ((baseUnit - applyTier(baseUnit, next)) / baseUnit) * 100,
                );
          nudge.innerHTML =
            pricePart +
            ' Add ' +
            remaining +
            ' more and get ' +
            pctOff +
            '% off!';
          nudge.hidden = false;
        } else if (eligible) {
          // Top tier reached — confirm the savings
          var curPct =
            tier && tier.type === 'percentage'
              ? tier.value
              : Math.ceil(((baseUnit - unit) / baseUnit) * 100);
          nudge.innerHTML =
            pricePart + ' You are now getting ' + curPct + '% off!';
          nudge.hidden = false;
        } else {
          nudge.hidden = true;
        }
      }
    });
  }

  function refreshAll() {
    return refreshCart().then(updateAll);
  }

  /* ---- Wire up ---- */
  if (document.readyState !== 'loading') refreshAll();
  else document.addEventListener('DOMContentLoaded', refreshAll);

  // The nudge is rendered inside the buttons block, but the quantity control is a separate
  // block higher up the form. Move the nudge to just above the quantity field.
  function placeNudge() {
    var nudge = document.querySelector('[data-td-nudge]');
    var anchor =
      document.querySelector('.product__block__quantity') ||
      document.querySelector('.quantity__wrapper');
    if (nudge && anchor && anchor.previousElementSibling !== nudge) {
      anchor.parentNode.insertBefore(nudge, anchor);
    }
  }
  if (document.readyState !== 'loading') placeNudge();
  else document.addEventListener('DOMContentLoaded', placeNudge);

  // Flag the page body when this product is eligible, so theme CSS/JS can react.
  function applyBodyEligibleClass() {
    var eligible = !!document.querySelector(
      '[data-button-price][data-tiered-eligible="true"]',
    );
    document.body.classList.toggle('discount-wrap', eligible);
  }
  if (document.readyState !== 'loading') applyBodyEligibleClass();
  else document.addEventListener('DOMContentLoaded', applyBodyEligibleClass);

  // Quantity + variant changes
  document.addEventListener('change', function (e) {
    if (e.target && (e.target.name === 'quantity' || e.target.name === 'id'))
      updateAll();
  });
  document.addEventListener('input', function (e) {
    if (e.target && e.target.name === 'quantity') updateAll();
  });

  // Theme +/- steppers update the input value in JS without firing change/input,
  // so bind them directly. Defer so the theme's own handler sets the value first.
  document.addEventListener('click', function (e) {
    var btn =
      e.target.closest &&
      e.target.closest('.quantity__button--plus, .quantity__button--minus');
    if (btn) setTimeout(updateAll, 30);
  });

  // Cart mutations — intercept fetch and XHR so we refresh after add/change/update/clear
  var CART_RE = /\/cart\/(add|change|update|clear)/;
  if (window.fetch) {
    var origFetch = window.fetch;
    window.fetch = function () {
      var url = (arguments[0] && arguments[0].url) || arguments[0] || '';
      return origFetch.apply(this, arguments).then(function (res) {
        if (typeof url === 'string' && CART_RE.test(url))
          setTimeout(refreshAll, 60);
        return res;
      });
    };
  }
  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (typeof url === 'string' && CART_RE.test(url)) {
      this.addEventListener('load', function () {
        setTimeout(refreshAll, 60);
      });
    }
    return origOpen.apply(this, arguments);
  };

  // Common theme events + a manual hook you can dispatch: document.dispatchEvent(new Event('tiered:refresh'))
  [
    'cart:updated',
    'cart:refresh',
    'cart:change',
    'variant:change',
    'variant:changed',
    'tiered:refresh',
  ].forEach(function (evt) {
    document.addEventListener(evt, function () {
      refreshAll();
    });
  });

  // Cart drawer on the same page: the fetch/XHR hooks above miss removals if the theme
  // captured its own transport before this script loaded. As a transport-independent
  // fallback, watch the theme's header cart-count element(s) — the theme updates them on
  // every add/remove/quantity change — and re-sync (debounced) whenever they mutate.
  var syncTimer = null;
  function scheduleSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(refreshAll, 120);
  }
  function watchCartCounts() {
    var els = document.querySelectorAll(
      '[data-header-cart-count],[data-cart-count],[data-cart-item-count],.cart-count,.cart-count-bubble',
    );
    els.forEach(function (el) {
      new MutationObserver(scheduleSync).observe(el.parentElement || el, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
      });
    });
  }
  if (document.readyState !== 'loading') watchCartCounts();
  else document.addEventListener('DOMContentLoaded', watchCartCounts);

  // Also re-sync when returning to the tab / restoring from bfcache
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) refreshAll();
  });
  window.addEventListener('pageshow', function () {
    refreshAll();
  });
})();
